import React, { useState, FormEvent, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import logoNewLogin from "../../assets/logo-newlogin.png";
import sparklingHeroNew from "../../assets/sparkling-hero-new.png";
import productReferenceNew from "../../assets/product-reference-new.jpg";
import { loginOtpVerify, verifyMail, getOtpThroughMail } from "../../api/login";
import OtpVerification from "./otpVerification";
import URL_MAPPING, {
  EMPLOYEE_PORTAL_URL,
  INTERNAL_FIORI_URL,
} from "../../routes/constants";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import { useNotifications } from "../../notification/index";
import { toast, ToastContainer } from "react-toastify";
import { getUserInfo, useRTL } from "../../utility/index";
import "./login.css";

const content = {
  en: {
    navHr: "HR Services Guide",
    navPortal: "Employee Portal",
    navFiori: "SAP Fiori",
    langToggle: "العربية",
    salesForce: "SALES FORCE",
    heroEyebrow: "BERAIN · SALES FORCE",
    heroTitle1: "Fresh energy.",
    heroTitle2: "Every day.",
    heroDesc: "Your people. Your customers. Your next opportunity.\nBring it all together with Berain.",
    heroBadgeCategory: "Signature Glass Series",
    heroBadgeTitle: "Berain Sparkling 270ml",
    heroBadgeDesc: "Premium Carbonated Water",
    heroCaption: "A little sparkle. A fresh perspective.",
    heroFoot: "CONNECTED BY PURPOSE",
    heroBrand: "بيرين",
    loginTag: "YOUR WORKDAY STARTS HERE",
    loginTitle: "Welcome back.",
    loginIntro: "Sign in to your Berain workspace.",
    emailLabel: "Work email",
    emailPlaceholder: "you@berain.com.sa",
    passwordLabel: "Password",
    passwordPlaceholder: "Enter your password",
    forgotPassword: "Forgot password?",
    show: "Show",
    hide: "Hide",
    keepSignedIn: "Keep me signed in",
    signIn: "Sign in",
    signingIn: "Signing in...",
    needHand: "Need a hand?",
    contactSupport: "Contact IT support ↗",
    employeeAccess: "Berain employee access",
    secureWorkspace: "Secure workspace ◇",
    footerCopyright: "© Berain · Together, we move forward.",
    footerExplore: "Explore the workspace ↗",
  },
  ar: {
    navHr: "دليل الموارد البشرية",
    navPortal: "بوابة الموظف",
    navFiori: "ساب فيوري",
    langToggle: "English",
    salesForce: "المبيعات",
    heroEyebrow: "بيرين · المبيعات",
    heroTitle1: "طاقة متجددة.",
    heroTitle2: "كل يوم.",
    heroDesc: "فريقك. عملاؤك. فرصتك القادمة.\nاجمع كل ذلك مع بيرين.",
    heroBadgeCategory: "المجموعة الزجاجية الفاخرة",
    heroBadgeTitle: "مياه بيرين الفوارة ٢٧٠ مل",
    heroBadgeDesc: "مياه طبيعية فوارة فاخرة",
    heroCaption: "بريق منعش. ورؤية جديدة.",
    heroFoot: "متصلون بالهدف",
    heroBrand: "بيرين",
    loginTag: "يبدأ يوم عملك هنا",
    loginTitle: "مرحباً بعودتك.",
    loginIntro: "سجّل الدخول إلى مساحة عمل بيرين.",
    emailLabel: "البريد الإلكتروني للعمل",
    emailPlaceholder: "you@berain.com.sa",
    passwordLabel: "كلمة المرور",
    passwordPlaceholder: "أدخل كلمة المرور",
    forgotPassword: "نسيت كلمة المرور؟",
    show: "إظهار",
    hide: "إخفاء",
    keepSignedIn: "إبقائي قيد تسجيل الدخول",
    signIn: "تسجيل الدخول",
    signingIn: "جارٍ تسجيل الدخول...",
    needHand: "هل تحتاج مساعدة؟",
    contactSupport: "تواصل مع الدعم الفني ↗",
    employeeAccess: "دخول موظفي بيرين",
    secureWorkspace: "مساحة عمل آمنة ◇",
    footerCopyright: "© بيرين · معاً، نمضي قدماً.",
    footerExplore: "استكشف مساحة العمل ↗",
  },
};

const Index: React.FC = () => {
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [loader, setLoader] = useState<boolean>(false);
  const [optScreen, setOtpScreen] = useState<boolean>(false);
  const [methodType, setMethodType] = useState<string>("MFA");
  const [timeLeft, setTimeLeft] = useState<number>(300);

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { callingSseEvent } = useNotifications();

  // Read initial language safely from localStorage or i18n
  const [isRTL, setIsRTL] = useState<boolean>(() => {
    if (typeof window !== "undefined" && window.localStorage) {
      const saved = localStorage.getItem("lang");
      if (saved === "Arabic" || saved === "ar") return true;
      if (saved === "English" || saved === "en") return false;
    }
    return i18n.language === "ar";
  });

  // Keep DOM dir synchronized at component top level
  useRTL(isRTL);

  useEffect(() => {
    const targetLng = isRTL ? "ar" : "en";
    if (i18n.language !== targetLng) {
      i18n.changeLanguage(targetLng);
    }
    document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");
  }, [isRTL, i18n]);

  const text = isRTL ? content.ar : content.en;

  const handleToggleLanguage = () => {
    const nextIsArabic = !isRTL;
    const newLang = nextIsArabic ? "Arabic" : "English";
    const newLngCode = nextIsArabic ? "ar" : "en";
    localStorage.setItem("lang", newLang);
    i18n.changeLanguage(newLngCode);
    setIsRTL(nextIsArabic);
    document.documentElement.setAttribute("dir", nextIsArabic ? "rtl" : "ltr");
  };

  const handleNavigate = (): void => {
    navigate(URL_MAPPING.SIGNUP, { state: { isForgetPass: true } });
  };

  const handleBack = (): void => {
    setOtpScreen(false);
  };

  const handleUserMapToRoute = (roles: string[]): void => {
    if (!Array.isArray(roles)) return;

    if (
      roles.includes("NWC_Admin") ||
      roles.includes("NWC_Inventory") ||
      roles.includes("NWC_LH_Driver")
    ) {
      navigate(URL_MAPPING.INVENTORY);
    } else if (roles.includes("NWC_Finance")) {
      navigate(URL_MAPPING.FINANCE);
    } else if (roles.includes("NWC_Address")) {
      navigate(URL_MAPPING.MANAGEADDR);
    } else if (
      roles.includes("NWC_C_Order") ||
      roles.includes("NWC_U_Order") ||
      roles.includes("NWC_D_Order")
    ) {
      navigate(URL_MAPPING.CREATEORDER);
    } else if (roles.includes("NWC_R_Order")) {
      navigate(URL_MAPPING.ORDERHISTORY);
    }
  };

  const handleOtpVerification = async (otp: any): Promise<void> => {
    const otpTo = otp.join("");
    setLoader(true);
    const data: any = {
      email: username,
      password: password,
      auth_method: methodType,
    };
    if (methodType === "MFA") {
      data.mfa_code = otpTo;
    }
    if (methodType === "EMAIL_OTP") {
      data.otp = otpTo;
    }
    await loginOtpVerify(data)
      .then((res) => {
        Cookies.set("token", res?.data?.access_token);
        Cookies.set("refresh_token", res?.data?.refresh_token);
        callingSseEvent(res?.data?.access_token);
        const user_info = getUserInfo();

        if (user_info.role === "NWC" && user_info.nwc_role?.length) {
          localStorage.setItem("nwc_role", JSON.stringify(user_info.nwc_role));
        }

        if (user_info.role === "NWC") {
          localStorage.setItem("section", "Zam Zam");
          const storedRoles = JSON.parse(
            localStorage.getItem("nwc_role") || "[]"
          );
          handleUserMapToRoute(storedRoles);
        } else {
          navigate(URL_MAPPING.LANDING);
        }
      })
      .catch((err) => {
        console.log(err);
        setLoader(false);
        if (err?.response?.data?.detail?.error_code === "password_expired") {
          setError("pass_expired");
        } else {
          setError("wrong_all_cred");
        }
      });
  };

  const handleSubmit = async (event: FormEvent): Promise<void> => {
    setMethodType("MFA");
    event.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!username) {
      return setError("enter_email");
    } else if (!emailRegex.test(username)) {
      return setError("valid_email");
    } else {
      setError("");
    }
    if (!password) {
      return setError("enter_password");
    } else {
      setError("");
    }

    setLoader(true);
    await verifyMail({ email: username })
      .then((res) => {
        if (res?.data?.mfa_enabled && res?.data?.password_enabled) {
          setOtpScreen(true);
        } else {
          setError("wrong_cred");
        }
      })
      .catch(() => {
        setError("wrong_cred");
      });

    setLoader(false);
  };

  useEffect(() => {
    if (Cookies.get("token")) {
      const storedRoles = JSON.parse(
        localStorage.getItem("nwc_role") || "[]"
      );
      if (getUserInfo()?.role === "NWC") {
        handleUserMapToRoute(storedRoles);
      } else {
        navigate(URL_MAPPING.LANDING);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const errotTypeTranslation = (errorStr: string) => {
    switch (errorStr) {
      case "enter_email":
        return t("LoginError.entaerEmail");
      case "enter_password":
        return t("LoginError.enterPassword");
      case "valid_email":
        return t("LoginError.enterValidEmail");
      case "wrong_cred":
        return t("LoginError.wrongCred");
      case "wrong_all_cred":
        return t("LoginError.wrongAllCred");
      case "pass_expired":
        return (
          <p>
            {t("LoginError.passExpired")}{" "}
            <Link
              className="text-[#792080] underline font-semibold"
              to={URL_MAPPING.SIGNUP}
              state={{ isResetPass: true }}
            >
              {t("LoginError.resetHere")}
            </Link>
          </p>
        );
      default:
        return null;
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleSubmit(event);
    }
  };

  const handleGetOtpThroughMail = async () => {
    setTimeLeft(300);
    setError("");
    await getOtpThroughMail(username)
      .then(() => {
        setMethodType("EMAIL_OTP");
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.detail?.message || "Error sending an email"
        );
        console.log(err?.response?.data?.detail?.message);
      });
  };

  return (
    <>
      <ToastContainer />
      <div
        className="berain-login-page h-screen max-h-screen w-full flex flex-col justify-between overflow-hidden selection:bg-[#00a9a9] selection:text-white"
        dir={isRTL ? "rtl" : "ltr"}
      >
        {/* Top Header - Compact 56px-64px */}
        <header className="h-14 md:h-16 flex items-center justify-between px-6 md:px-[4vw] bg-white border-b border-[#edf2f1] shrink-0 z-20">
          <Link
            to={URL_MAPPING.LOGIN_ROOT}
            className="flex items-center group"
            aria-label="Berain"
          >
            <img
              src={logoNewLogin}
              alt="Berain بيرين"
              className="w-20 md:w-24 h-9 md:h-10 object-contain transition-transform group-hover:scale-105"
            />
          </Link>

          <nav
            aria-label="Employee services"
            className="flex items-center gap-3 sm:gap-5 md:gap-6 text-[11px] md:text-xs font-medium text-[#173e43]"
          >
            <Link
              to={URL_MAPPING.HR_SERVICES_GUIDE}
              className="hover:text-[#00a9a9] transition-colors flex items-center gap-1"
            >
              <span>{text.navHr}</span>
              <span className="text-[10px]">↗</span>
            </Link>

            <a
              href={EMPLOYEE_PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#00a9a9] transition-colors hidden sm:flex items-center gap-1"
            >
              <span>{text.navPortal}</span>
              <span className="text-[10px]">↗</span>
            </a>

            <a
              href={INTERNAL_FIORI_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#00a9a9] transition-colors hidden md:flex items-center gap-1"
            >
              <span>{text.navFiori}</span>
              <span className="text-[10px]">↗</span>
            </a>

            <button
              id="language"
              type="button"
              onClick={handleToggleLanguage}
              className="border border-[#dce7e5] bg-white hover:bg-[#fafcfb] active:scale-95 transition-all text-[#173e43] rounded-full px-3.5 py-1 text-xs font-semibold shadow-xs cursor-pointer ml-1"
            >
              {text.langToggle}
            </button>
          </nav>
        </header>

        {/* Main Content: Split Grid - Flex 1 & min-h-0 to guarantee fit without scrollbar */}
        <main className="flex-1 min-h-0 h-full grid grid-cols-1 lg:grid-cols-[56%_44%] overflow-hidden">
          {/* Left Hero Section */}
          <section className="relative bg-[#e7f3f0] overflow-hidden p-6 md:p-8 lg:p-10 pb-4 flex flex-col justify-between h-full min-h-0 select-none">
            {/* Hero Copy */}
            <div className="relative z-10 max-w-lg pointer-events-none select-none">
              <div className="text-[9px] md:text-[10px] tracking-[2.5px] font-bold text-[#537b7d] uppercase mb-2 md:mb-3">
                {text.heroEyebrow}
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[46px] xl:text-[54px] font-medium text-[#173e43] tracking-[-1.5px] md:tracking-[-2.5px] leading-[1.06] m-0">
                {text.heroTitle1}<br />
                <em className="font-serif italic font-normal text-[#008f93] tracking-normal">
                  {text.heroTitle2}
                </em>
              </h1>
              <p className="text-xs md:text-sm text-[#537b7d] leading-relaxed mt-2 md:mt-3 max-w-sm sm:max-w-md whitespace-pre-line">
                {text.heroDesc}
              </p>
            </div>

            {/* Signature Glass Bottle Preview Card (Integrating product-reference-new asset) */}
            <div className="relative z-10 my-2 hidden xl:inline-flex items-center gap-2.5 bg-white/75 backdrop-blur-md rounded-xl p-2 px-3 border border-white/70 shadow-xs max-w-xs transition-transform hover:scale-102">
              <img
                src={productReferenceNew}
                alt="Berain Sparkling 270ml Glass Bottle"
                className="w-7 h-10 object-contain drop-shadow-sm"
              />
              <div className={isRTL ? "text-right" : "text-left"}>
                <div className="text-[8.5px] uppercase tracking-wider text-[#008f93] font-bold">
                  {text.heroBadgeCategory}
                </div>
                <div className="text-[11px] font-semibold text-[#173e43] leading-tight">
                  {text.heroBadgeTitle}
                </div>
                <div className="text-[8.5px] text-[#728385]">
                  {text.heroBadgeDesc}
                </div>
              </div>
            </div>

            {/* Sparkling Bottle Hero Visual (Integrating sparkling-hero-new asset) */}
            <div className="absolute right-0 bottom-0 w-[62%] sm:w-[55%] lg:w-[58%] h-[78%] sm:h-[82%] pointer-events-none select-none">
              <img
                src={sparklingHeroNew}
                alt="Berain sparkling water in a clear glass bottle"
                className="berain-hero-mask w-full h-full object-cover object-center"
              />
              <span className="writing-mode-vertical absolute bottom-12 right-3 md:right-4 text-[9px] tracking-[1.5px] text-[#537b7d] font-medium hidden sm:block">
                {text.heroCaption}
              </span>
            </div>

            {/* Hero Footer */}
            <div className="relative z-10 flex items-center gap-3 text-[8.5px] md:text-[9px] tracking-[2px] font-medium text-[#537b7d] uppercase mt-3 select-none">
              <span>{text.heroFoot}</span>
              <span className="w-12 h-px bg-[#a4c6c4]" />
              <span className="font-normal font-sans tracking-normal text-xs text-[#537b7d]">
                {text.heroBrand}
              </span>
            </div>
          </section>

          {/* Right Login Section */}
          <section className="bg-white p-6 md:p-8 lg:p-10 flex flex-col justify-between h-full min-h-0 overflow-hidden">
            {optScreen === false ? (
              <div className="w-full max-w-[360px] mx-auto my-auto py-1">
                {/* Portal Badge */}
                <div className="flex items-center gap-2 text-[8.5px] md:text-[9px] font-bold tracking-[2px] text-[#728385] uppercase mb-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00a9a9]" />
                  <span>{text.loginTag}</span>
                </div>

                {/* Heading & Intro */}
                <h2 className="text-2xl md:text-[28px] font-medium text-[#173e43] tracking-tight leading-tight mb-0.5">
                  {text.loginTitle}
                </h2>
                <p className="text-xs text-[#728385] mb-3 md:mb-4">
                  {text.loginIntro}
                </p>

                {/* Login Form */}
                <form id="login-form" onSubmit={handleSubmit} className="space-y-3">
                  {/* Work Email */}
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-[11px] md:text-xs font-semibold text-[#173e43] mb-1"
                    >
                      {text.emailLabel}
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder={text.emailPlaceholder}
                      autoComplete="username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      onKeyDown={handleKeyDown}
                      required
                      className="w-full h-10 md:h-11 bg-[#fafcfb] border border-[#dce5e3] rounded-lg px-3.5 text-xs md:text-sm text-[#173e43] placeholder-[#94a3b8] focus:border-[#00a9a9] focus:ring-1 focus:ring-[#00a9a9] focus:outline-none transition-colors"
                    />
                  </div>

                  {/* Password */}
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label
                        htmlFor="password"
                        className="text-[11px] md:text-xs font-semibold text-[#173e43] m-0"
                      >
                        {text.passwordLabel}
                      </label>
                      <button
                        type="button"
                        id="forgot"
                        onClick={handleNavigate}
                        className="text-[11px] text-[#792080] hover:underline font-medium cursor-pointer"
                      >
                        {text.forgotPassword}
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder={text.passwordPlaceholder}
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyDown={handleKeyDown}
                        required
                        className={`w-full h-10 md:h-11 bg-[#fafcfb] border border-[#dce5e3] rounded-lg ${
                          isRTL ? "pl-14 pr-3.5" : "pr-14 pl-3.5"
                        } text-xs md:text-sm text-[#173e43] placeholder-[#94a3b8] focus:border-[#00a9a9] focus:ring-1 focus:ring-[#00a9a9] focus:outline-none transition-colors`}
                      />
                      <button
                        type="button"
                        id="show-password"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        aria-pressed={showPassword}
                        className={`absolute top-1/2 transform -translate-y-1/2 ${
                          isRTL ? "left-3" : "right-3"
                        } text-[11px] font-medium text-[#728385] hover:text-[#173e43] cursor-pointer`}
                      >
                        {showPassword ? text.hide : text.show}
                      </button>
                    </div>
                  </div>

                  {/* Error Notification */}
                  {error && (
                    <div className="text-red-600 text-xs text-left bg-red-50 p-2 rounded-md border border-red-200">
                      {errotTypeTranslation(error)}
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loader}
                    className="w-full h-10 md:h-11 bg-[#792080] hover:bg-[#68196e] active:scale-[0.99] transition-all text-white font-semibold text-xs md:text-sm rounded-lg flex items-center justify-between px-4 cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed shadow-sm"
                  >
                    <span>{loader ? text.signingIn : text.signIn}</span>
                    <span className="text-base font-normal">{isRTL ? "←" : "→"}</span>
                  </button>
                </form>
              </div>
            ) : (
              <div className="w-full max-w-[360px] mx-auto my-auto py-1">
                <OtpVerification
                  className="w-full p-0 sm:p-1"
                  hideLogo={true}
                  handleSubmit={handleOtpVerification}
                  loader={loader}
                  handleBack={handleBack}
                  error={errotTypeTranslation(error)}
                  handleGetOtpThroughMail={handleGetOtpThroughMail}
                  methodType={methodType}
                  timeLeft={timeLeft}
                  setTimeLeft={setTimeLeft}
                  username={username}
                />
              </div>
            )}
          </section>
        </main>
      </div>
    </>
  );
};

export default Index;
